import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BodyPortal } from '@/components/BodyPortal';
import { tv } from '@/utils/tv';
import { Check } from '@/components/Icons/Check';
import {
  ChevronRightIcon,
  ContrastIcon,
  MoonIcon,
  SunIcon,
  SystemIcon,
} from '@/components/Icons/SidebarIcons';
import { useTheme } from '@/hooks/useTheme';
import { THEMES, type Theme } from '@/types/theme';

const POPOVER_WIDTH = 260;
const POPOVER_GAP = 4;
const ANIMATION_DURATION_MS = 280;

const themeOption = tv({
  base: 'flex w-full cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 text-subhead-14 transition-colors',
  variants: {
    isSelected: {
      true: 'bg-info-surface text-primary',
      false: 'text-muted-foreground hover:bg-hover',
    },
  },
});

interface PopoverPosition {
  top: number;
  left: number;
}

const themeIcons: Record<Theme, React.ReactNode> = {
  light: <SunIcon className="size-5" />,
  dark: <MoonIcon className="size-5" />,
  contrast: <ContrastIcon className="size-5" />,
  system: <SystemIcon className="size-5" />,
};

export const ThemeSwitcher: React.FC = () => {
  const { t } = useTranslation();
  const { theme, setTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [position, setPosition] = useState<PopoverPosition | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLUListElement>(null);

  const onToggle = () => {
    if (isOpen) {
      setIsClosing(true);
      return;
    }
    setIsOpen(true);
  };

  const onSelectTheme = (value: Theme) => {
    setTheme(value);
    setIsClosing(false);
    setIsOpen(false);
  };

  useEffect(() => {
    if (!isClosing) {
      return;
    }

    const timer = window.setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
    }, ANIMATION_DURATION_MS);

    return () => clearTimeout(timer);
  }, [isClosing]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const updatePosition = () => {
      if (!triggerRef.current) {
        return;
      }
      const rect = triggerRef.current.getBoundingClientRect();
      const sidebar = triggerRef.current.closest('aside');
      const sidebarRight = sidebar?.getBoundingClientRect().right ?? rect.right;
      setPosition({
        top: rect.bottom,
        left: sidebarRight + POPOVER_GAP,
      });
    };
    updatePosition();

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        popoverRef.current?.contains(target)
      ) {
        return;
      }
      setIsClosing(true);
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsClosing(true);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={onToggle}
        aria-haspopup="menu"
        aria-expanded={isOpen && !isClosing}
        className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-2xl px-[15px] py-[13px] text-subhead-14 text-muted-foreground transition-colors hover:bg-hover lg:justify-start"
      >
        <span className="flex size-6 shrink-0 items-center justify-center">
          {themeIcons[theme]}
        </span>
        <span className="hidden flex-1 truncate text-left lg:inline">
          {t('sidebar.tabs.themeSwitcher')}
        </span>
        <ChevronRightIcon className="hidden size-4 lg:inline" />
      </button>

      {isOpen && position && (
        <BodyPortal>
          <div
            style={{
              top: position.top,
              left: position.left,
              transform: 'translateY(-100%)',
            }}
            className="fixed z-20"
          >
            <ul
              ref={popoverRef}
              role="menu"
              aria-label={t('sidebar.aria.themeMenu')}
              style={{
                width: POPOVER_WIDTH,
                animation: isClosing
                  ? `popover-slide-out-left ${ANIMATION_DURATION_MS}ms ease-in forwards`
                  : `popover-slide-in-left ${ANIMATION_DURATION_MS}ms ease-out`,
              }}
              className="rounded-2xl bg-card p-2 shadow-card"
            >
              {THEMES.map((value) => {
                const isSelected = value === theme;

                return (
                  <li key={value}>
                    <button
                      type="button"
                      role="menuitemradio"
                      aria-checked={isSelected}
                      onClick={() => onSelectTheme(value)}
                      className={themeOption({ isSelected })}
                    >
                      <span className="flex size-5 shrink-0 items-center justify-center">
                        {themeIcons[value]}
                      </span>
                      <span className="flex-1 text-left">
                        {t(`sidebar.theme.${value}`)}
                      </span>
                      {isSelected && (
                        <Check className="size-4 shrink-0 text-primary" />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </BodyPortal>
      )}
    </>
  );
};
